import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PostModal from './components/PostModal';
import CreatePostModal from './components/CreatePostModal';
import EditPostModal from './components/EditPostModal';
import CalendarView from './components/CalendarView';
import { 
  getSocialMediaPosts, 
  createSocialMediaPost, 
  updateSocialMediaPost, 
  deleteSocialMediaPost 
} from '../../services/socialMediaService';
import { useAuth } from '../../AuthContext';
import { addScopeChangeListener, getEffectiveAccountId, isClient } from '../../services/authorizationService';
import { 
  Eye, 
  PencilSimple, 
  CheckCircle, 
  PaperPlaneTilt
} from '@phosphor-icons/react';
import { 
  FacebookIcon, 
  InstagramIcon, 
  LinkedInIcon, 
  XIcon 
} from '../../components/SocialMediaIcons';

export default function SocialMediaDashboard() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [selectedPost, setSelectedPost] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load posts from Firestore on component mount
  useEffect(() => {
    const loadPosts = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const postsData = await getSocialMediaPosts();
        setPosts(postsData);
      } catch (err) {
        console.error('Error loading social media posts:', err);
        // Only show error for actual failures, not for missing account scope or permissions
        if (err.message && !err.message.includes('No account scope') && !err.message.includes('Insufficient permissions')) {
          setError('Failed to load posts. Please try again.');
        } else {
          // For account scope or permission issues, just set empty posts
          setPosts([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadPosts();
  }, [currentUser]);

  // Listen for account scope changes and reload posts
  useEffect(() => {
    const unsubscribe = addScopeChangeListener((scopeData) => {
      console.log('Account scope changed in Social Media:', scopeData);
      // Reload posts when scope changes
      if (scopeData.effectiveAccountId && currentUser) {
        const loadPosts = async () => {
          try {
            setIsLoading(true);
            setError(null);
            const postsData = await getSocialMediaPosts();
            setPosts(postsData);
          } catch (err) {
            console.error('Error loading social media posts after scope change:', err);
            // Only show error for actual failures, not for missing account scope or permissions
            if (err.message && !err.message.includes('No account scope') && !err.message.includes('Insufficient permissions')) {
              setError('Failed to load posts. Please try again.');
            } else {
              // For account scope or permission issues, just set empty posts
              setPosts([]);
            }
          } finally {
            setIsLoading(false);
          }
        };
        loadPosts();
      }
    });

    return unsubscribe;
  }, [currentUser]);

  // Handle opening post from notification
  useEffect(() => {
    if (location.state?.openPostId && posts.length > 0) {
      const post = posts.find(p => p.id === location.state.openPostId);
      if (post) {
        setSelectedPost(post);
        // Clear the state to prevent re-opening on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, posts]);

  const now = new Date();
  const filteredPosts = posts.filter(post => {
    const postDate = new Date(post.date);
    
    switch (filter) {
      case 'upcoming':
        return postDate >= now;
      case 'past':
        return postDate < now;
      case 'draft':
        return post.status === 'Draft';
      case 'scheduled':
        return post.status === 'Scheduled';
      case 'pending':
        return post.status === 'Pending Approval';
      case 'approved':
        return post.status === 'Approved';
      case 'posted':
        return post.status === 'Posted';
      default:
        return true; // 'all'
    }
  });

  // Calculate status summary from actual posts
  const statusData = [
    { 
      label: "Pending Approval", 
      count: posts.filter(p => p.status === 'Pending Approval').length, 
      color: "text-yellow-500" 
    },
    { 
      label: "Approved", 
      count: posts.filter(p => p.status === 'Approved').length, 
      color: "text-green-600" 
    },
    { 
      label: "Scheduled", 
      count: posts.filter(p => p.status === 'Scheduled').length, 
      color: "text-blue-600" 
    },
    { 
      label: "Published This Week", 
      count: posts.filter(p => {
        const postDate = new Date(p.date);
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return postDate < now && postDate >= weekAgo;
      }).length, 
      color: "text-gray-600" 
    },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleCreatePost = async (newPost) => {
    try {
      const createdPost = await createSocialMediaPost(newPost);
      setPosts(prev => [createdPost, ...prev]);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating post:', error);
      // Show specific error message based on the error type
      if (error.message?.includes('No account scope')) {
        setError('You need to be assigned to an account before creating posts. Please contact your administrator.');
      } else if (error.message?.includes('Insufficient permissions')) {
        setError('You do not have permission to create posts. Please contact your administrator.');
      } else {
        setError('Failed to create post. Please try again.');
      }
    }
  };

  const handleEditPost = async (updatedPost) => {
    try {
      await updateSocialMediaPost(updatedPost.id, updatedPost);
      setPosts(prev => prev.map(p => p.id === updatedPost.id ? updatedPost : p));
      setShowEditModal(false);
      setEditingPost(null);
    } catch (error) {
      console.error('Error updating post:', error);
      setError('Failed to update post. Please try again.');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      // Find the post to get its image URL
      const postToDelete = posts.find(p => p.id === postId);
      const imageUrl = postToDelete?.image;
      
      await deleteSocialMediaPost(postId, imageUrl);
      setPosts(prev => prev.filter(p => p.id !== postId));
      setShowEditModal(false);
      setEditingPost(null);
    } catch (error) {
      console.error('Error deleting post:', error);
      setError('Failed to delete post. Please try again.');
    }
  };

  const handleApprovePost = async (postId) => {
    try {
      const updatedPost = { status: 'Approved', statusColor: 'bg-green-500' };
      await updateSocialMediaPost(postId, updatedPost);
      setPosts(prev => prev.map(p => 
        p.id === postId 
          ? { ...p, ...updatedPost }
          : p
      ));
    } catch (error) {
      console.error('Error approving post:', error);
      setError('Failed to approve post. Please try again.');
    }
  };

  const handleMarkAsPosted = async (postId) => {
    try {
      const updatedPost = { status: 'Posted', statusColor: 'bg-green-500' };
      await updateSocialMediaPost(postId, updatedPost);
      setPosts(prev => prev.map(p => 
        p.id === postId 
          ? { ...p, ...updatedPost }
          : p
      ));
    } catch (error) {
      console.error('Error marking post as posted:', error);
      setError('Failed to mark post as posted. Please try again.');
    }
  };


  const openEditModal = (post) => {
    setEditingPost(post);
    setShowEditModal(true);
  };

  const generateSocialMediaUrl = (post) => {
    switch (post.platform) {
      case 'facebook':
        return `https://www.facebook.com/`;
      case 'instagram':
        return `https://www.instagram.com/`;
      case 'linkedin':
        return `https://www.linkedin.com/feed/`;
      case 'twitter':
        return `https://twitter.com/compose/tweet`;
      default:
        return '#';
    }
  };

  const handlePostNow = async (post) => {
    const url = generateSocialMediaUrl(post);
    window.open(url, '_blank');
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'facebook':
        return FacebookIcon;
      case 'instagram':
        return InstagramIcon;
      case 'linkedin':
        return LinkedInIcon;
      case 'twitter':
        return XIcon;
      default:
        return FacebookIcon;
    }
  };

  const getPlatformIconById = (iconId) => {
    switch (iconId) {
      case 'facebook':
        return FacebookIcon;
      case 'instagram':
        return InstagramIcon;
      case 'linkedin':
        return LinkedInIcon;
      case 'x':
        return XIcon;
      default:
        return FacebookIcon;
    }
  };

  const getActionButtons = (post) => {
    const buttons = [
      {
        label: 'View Details',
        icon: Eye,
        color: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
        action: () => setSelectedPost(post)
      }
    ];

    if (post.status === 'Pending Approval') {
      buttons.push(
        {
          label: 'Approve',
          icon: CheckCircle,
          color: 'bg-green-500 text-white hover:bg-green-600',
          action: () => handleApprovePost(post.id)
        }
      );
    } else if (post.status === 'Approved') {
      buttons.push(
        {
          label: 'Post Now',
          icon: PaperPlaneTilt,
          color: 'bg-blue-500 text-white hover:bg-blue-600',
          action: () => handlePostNow(post)
        },
        {
          label: 'Mark as Posted',
          icon: CheckCircle,
          color: 'bg-green-500 text-white hover:bg-green-600',
          action: () => handleMarkAsPosted(post.id)
        }
      );
    }

    // Add edit button for all posts
    buttons.push({
      label: 'Edit',
      icon: PencilSimple,
      color: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      action: () => openEditModal(post)
    });

    return buttons;
  };

  // Check if user has account assigned
  const hasAccountAssigned = getEffectiveAccountId() !== null;
  const showAccountWarning = isClient() && !hasAccountAssigned && !isLoading;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Social Media Management</h1>
        <p className="text-gray-500 mt-1">Manage your social media content and approval workflow</p>
      </div>

      {/* Account Assignment Warning */}
      {showAccountWarning && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-yellow-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-yellow-800 text-sm">
              <strong>Account Required:</strong> You need to be assigned to an account before you can create posts. Please contact your administrator to assign you to an account.
            </span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800 text-sm">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading social media posts...</p>
        </div>
      )}

      {/* Main Content - Only show when not loading */}
      {!isLoading && (
        <>
          {/* Status Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {statusData.map((item) => (
              <div key={item.label} className="bg-white rounded-lg shadow-sm border p-4 text-center">
                <p className="text-sm text-gray-500 mb-1">{item.label}</p>
                <p className={`text-2xl font-bold ${item.color}`}>{item.count}</p>
              </div>
            ))}
          </div>

      {/* Filter and View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label htmlFor="filter" className="text-sm font-medium text-gray-700">
              Filter:
            </label>
            <select
              id="filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Posts</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="posted">Posted</option>
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              List View
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'calendar'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Calendar View
            </button>
          </div>
          
          <button 
            onClick={() => setShowCreateModal(true)}
            disabled={showAccountWarning}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showAccountWarning 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
            title={showAccountWarning ? 'You need to be assigned to an account first' : 'Create a new post'}
          >
            + Create New Post
          </button>
        </div>
      </div>

      {/* Posts List or Calendar View */}
      {filteredPosts.length === 0 ? (
        <div className="text-center text-gray-500 py-12">
          <p className="text-lg">No posts found for the selected filter.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-2 text-blue-600 hover:underline"
          >
            Create your first post
          </button>
        </div>
      ) : viewMode === 'calendar' ? (
        <CalendarView posts={filteredPosts} onSelect={setSelectedPost} />
      ) : (
        <div className="space-y-4">
          {filteredPosts.map(post => (
            <div key={post.id} className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-start justify-between">
                {/* Left side - Image and content */}
                <div className="flex items-start space-x-4 flex-1">
                  {/* Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                    {post.image ? (
                      <img src={post.image} alt="Post" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      {/* Platform Icon */}
                      <div className={`w-6 h-6 rounded-full ${post.platformColor} flex items-center justify-center text-white`}>
                        {(() => {
                          const PlatformIcon = post.platformIcon ? getPlatformIconById(post.platformIcon) : getPlatformIcon(post.platform);
                          return <PlatformIcon size={14} />;
                        })()}
                      </div>
                      
                      {/* Status Badge */}
                      <span className={`px-2 py-1 rounded text-xs font-medium text-white ${post.statusColor}`}>
                        {post.status}
                      </span>
                    </div>

                    <p className="text-gray-900 mb-2 line-clamp-3">{post.caption}</p>


                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      {getActionButtons(post).map((button, index) => {
                        const IconComponent = button.icon;
                        return (
                          <button
                            key={index}
                            onClick={button.action}
                            className={`px-3 py-1 rounded text-xs font-medium transition-colors flex items-center space-x-1 ${button.color}`}
                          >
                            <IconComponent size={14} />
                            <span>{button.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Right side - Date */}
                <div className="text-sm text-gray-500 ml-4">
                  {formatDate(post.date)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Post Modal */}
      {selectedPost && (
        <PostModal 
          post={selectedPost} 
          onClose={() => setSelectedPost(null)} 
          onEdit={openEditModal}
        />
      )}

      {/* Create Post Modal */}
      {showCreateModal && (
        <CreatePostModal 
          onClose={() => setShowCreateModal(false)} 
          onSave={handleCreatePost}
        />
      )}

      {/* Edit Post Modal */}
      {showEditModal && editingPost && (
        <EditPostModal 
          post={editingPost}
          onClose={() => {
            setShowEditModal(false);
            setEditingPost(null);
          }} 
          onSave={handleEditPost}
          onDelete={handleDeletePost}
        />
      )}
        </>
      )}
    </div>
  );
}
